import React, { useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Grid,
  Switch,
  FormControlLabel,
  InputAdornment,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Warning as WarningIcon,
  DirectionsCar as CarIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useFleetCriteria, useUpdateFleetCriteria } from '../hooks/useFleetSubscriptions';

interface UpdateCriteriaDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

interface CriteriaFormData {
  min_vehicles: number;
  verification_period_days: number;
  grace_period_days: number;
  auto_downgrade_enabled: boolean;
  apply_to_existing: boolean;
  update_reason: string;
}

const validationSchema = yup.object({
  min_vehicles: yup
    .number()
    .required('Minimum vehicles is required')
    .min(1, 'Must be at least 1')
    .max(100, 'Cannot exceed 100'),
  verification_period_days: yup
    .number()
    .required('Verification period is required')
    .min(1, 'Must be at least 1 day')
    .max(365, 'Cannot exceed 365 days'),
  grace_period_days: yup
    .number()
    .required('Grace period is required')
    .min(0, 'Cannot be negative')
    .max(90, 'Cannot exceed 90 days'),
  auto_downgrade_enabled: yup.boolean().required(),
  apply_to_existing: yup.boolean().required(),
  update_reason: yup
    .string()
    .required('Update reason is required')
    .min(10, 'Reason must be at least 10 characters'),
});

const UpdateCriteriaDialog: React.FC<UpdateCriteriaDialogProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const { data: currentCriteria } = useFleetCriteria();
  const updateCriteria = useUpdateFleetCriteria();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CriteriaFormData>({
    resolver: yupResolver(validationSchema) as any,
    defaultValues: {
      min_vehicles: currentCriteria?.min_vehicles || 5,
      verification_period_days: currentCriteria?.verification_period_days || 14,
      grace_period_days: currentCriteria?.grace_period_days || 3,
      auto_downgrade_enabled: currentCriteria?.auto_downgrade_enabled ?? true,
      apply_to_existing: true,
      update_reason: '',
    },
  });

  useEffect(() => {
    if (open && currentCriteria) {
      reset({
        min_vehicles: currentCriteria.min_vehicles,
        verification_period_days: currentCriteria.verification_period_days,
        grace_period_days: currentCriteria.grace_period_days,
        auto_downgrade_enabled: currentCriteria.auto_downgrade_enabled,
        apply_to_existing: true,
        update_reason: '',
      });
    }
  }, [open, currentCriteria, reset]);

  const onSubmit = async (data: CriteriaFormData) => {
    try {
      await updateCriteria.mutateAsync({
        min_vehicles: data.min_vehicles,
        verification_period_days: data.verification_period_days,
        grace_period_days: data.grace_period_days,
        auto_downgrade_enabled: data.auto_downgrade_enabled,
        apply_to_existing: data.apply_to_existing,
        update_reason: data.update_reason,
      });

      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      console.error('Update criteria error:', error);
    }
  };

  const handleClose = () => {
    if (!updateCriteria.isPending) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SettingsIcon sx={{ color: 'primary.main' }} />
          <Typography variant="h6">Update Fleet Manager Criteria</Typography>
        </Box>
      </DialogTitle>

      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          {/* Current Values Display */}
          {currentCriteria && (
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>Current:</strong> Min {currentCriteria.min_vehicles} vehicles •{' '}
                {currentCriteria.verification_period_days}-day verification •{' '}
                {currentCriteria.grace_period_days}-day grace period •{' '}
                Auto-downgrade: {currentCriteria.auto_downgrade_enabled ? 'ON' : 'OFF'}
              </Typography>
            </Alert>
          )}

          <Grid container spacing={3}>
            {/* Minimum Vehicles */}
            <Grid item xs={12} md={6}>
              <Controller
                name="min_vehicles"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Minimum Vehicles for Fleet Manager"
                    type="number"
                    fullWidth
                    required
                    error={!!errors.min_vehicles}
                    helperText={
                      errors.min_vehicles?.message ||
                      'Number of vehicles required to automatically qualify as Fleet Manager'
                    }
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <CarIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
            </Grid>

            {/* Verification Period */}
            <Grid item xs={12} md={6}>
              <Controller
                name="verification_period_days"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Verification Period (days)"
                    type="number"
                    fullWidth
                    required
                    error={!!errors.verification_period_days}
                    helperText={
                      errors.verification_period_days?.message ||
                      'How long verification status is valid'
                    }
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <ScheduleIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
            </Grid>

            {/* Grace Period */}
            <Grid item xs={12} md={6}>
              <Controller
                name="grace_period_days"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Grace Period Before Downgrade (days)"
                    type="number"
                    fullWidth
                    required
                    error={!!errors.grace_period_days}
                    helperText={
                      errors.grace_period_days?.message ||
                      'Buffer period before auto-downgrade takes effect'
                    }
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <ScheduleIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
            </Grid>

            {/* Auto-Downgrade Toggle */}
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  p: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Controller
                  name="auto_downgrade_enabled"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={field.value}
                          onChange={field.onChange}
                          color="success"
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            Enable Auto-Downgrade
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Automatically downgrade fleet managers who no longer qualify
                          </Typography>
                        </Box>
                      }
                    />
                  )}
                />
              </Box>
            </Grid>

            {/* Apply to Existing Toggle */}
            <Grid item xs={12}>
              <Box
                sx={{
                  p: 2,
                  border: '1px solid',
                  borderColor: 'warning.main',
                  borderRadius: 1,
                  backgroundColor: 'rgba(245, 158, 11, 0.05)',
                }}
              >
                <Controller
                  name="apply_to_existing"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={field.value}
                          onChange={field.onChange}
                          color="warning"
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            Apply Changes to Existing Fleet Managers
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            When enabled, these criteria changes will immediately affect all existing fleet managers.
                            When disabled, changes only apply to new fleet manager qualifications going forward.
                          </Typography>
                        </Box>
                      }
                    />
                  )}
                />
              </Box>
            </Grid>
          </Grid>

          {/* Update Reason */}
          <Box sx={{ mt: 3 }}>
            <Controller
              name="update_reason"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Reason for Update *"
                  multiline
                  rows={3}
                  fullWidth
                  required
                  placeholder="e.g., Adjusting criteria based on market conditions, Increasing requirements for quality control, etc."
                  error={!!errors.update_reason}
                  helperText={
                    errors.update_reason?.message ||
                    'Explain why these criteria are being updated (for audit trail)'
                  }
                />
              )}
            />
          </Box>

          {/* Warning Alert */}
          <Alert severity="warning" icon={<WarningIcon />} sx={{ mt: 3 }}>
            <Typography variant="body2">
              <strong>Important:</strong> Changing these criteria will affect ALL existing fleet
              managers. Those who no longer meet the requirements will enter the grace period
              and may be downgraded if auto-downgrade is enabled.
            </Typography>
          </Alert>

          {/* Error Alert */}
          {updateCriteria.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {updateCriteria.error?.message || 'Failed to update criteria'}
            </Alert>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} disabled={updateCriteria.isPending}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={updateCriteria.isPending}
            startIcon={
              updateCriteria.isPending ? <CircularProgress size={20} /> : <SettingsIcon />
            }
          >
            {updateCriteria.isPending ? 'Updating...' : 'Update Criteria'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default UpdateCriteriaDialog;
